import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';

/**
 * ROAD TO IRON MAN — the program board.
 *
 * THE_PLAN.md at the repo root is the human-authored source; this renders its
 * structured projection from `program_items`, the same rows JARVIS reads through
 * his `program` tool. One board, two readers, so they can never disagree.
 *
 * Canvas/CSS only, no WebGL (house rule).
 */

type Item = {
  id: string;
  phase: string;
  lane: string;
  title: string;
  detail: string | null;
  status: 'todo' | 'doing' | 'done' | 'blocked' | 'dropped';
  cadence: 'once' | 'daily' | 'weekly' | 'monthly';
  note: string | null;
  last_done_at: string | null;
  done_count: number;
  gate: string | null;
};

type ProgramResponse = {
  items: Item[];
  phase_titles: Record<string, string>;
  daily_status: string;
  count: number;
};

const PHASE_ORDER = ['daily', 'phase0', 'phase1', 'phase2', 'phase3', 'phase4'];

const LANE_COLOR: Record<string, string> = {
  CAPITAL: 'text-emerald-300 border-emerald-400/30',
  INSTRUMENT: 'text-sky-300 border-sky-400/30',
  POWER: 'text-amber-300 border-amber-400/30',
  'SUIT-HARDWARE': 'text-rose-300 border-rose-400/30',
  JARVIS: 'text-jarvis-blue border-jarvis-blue/30',
  STUDY: 'text-violet-300 border-violet-400/30',
};

const STATUS_NEXT: Record<Item['status'], Item['status']> = {
  todo: 'doing',
  doing: 'done',
  done: 'todo',
  blocked: 'doing',
  dropped: 'todo',
};

function isDoneToday(item: Item): boolean {
  if (!item.last_done_at) return false;
  return item.last_done_at.slice(0, 10) === new Date().toISOString().slice(0, 10);
}

export default function ProgramPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ProgramResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<ProgramResponse>('/program'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not reach the board');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const items = data?.items ?? [];

  const currentPhase = useMemo(() => {
    for (const ph of PHASE_ORDER) {
      if (ph === 'daily') continue;
      const rows = items.filter((i) => i.phase === ph);
      if (rows.length && rows.some((i) => i.status !== 'done' && i.status !== 'dropped')) {
        return ph;
      }
    }
    return null;
  }, [items]);

  const progress = useMemo(() => {
    const arc = items.filter((i) => i.phase !== 'daily');
    const done = arc.filter((i) => i.status === 'done').length;
    return { done, total: arc.length, pct: arc.length ? (done / arc.length) * 100 : 0 };
  }, [items]);

  const cycle = async (item: Item) => {
    setBusy(item.id);
    try {
      await api.patch(`/program/${item.id}`, { status: STATUS_NEXT[item.status] });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'update failed');
    } finally {
      setBusy(null);
    }
  };

  const markDone = async (item: Item) => {
    setBusy(item.id);
    try {
      await api.patch(`/program/${item.id}`, { mark_done: true });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'update failed');
    } finally {
      setBusy(null);
    }
  };

  const daily = items.filter((i) => i.phase === 'daily');

  return (
    // h-screen + overflow-y-auto, not min-h-screen: the app shell constrains
    // the viewport, so a min-h page taller than the screen simply clipped and
    // the board could not be scrolled past Phase 0 (Carter, 2026-07-25).
    <div className="h-screen overflow-y-auto bg-jarvis-deep-dark text-white font-mono">
      <header className="border-b border-jarvis-blue/15 px-6 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-[10px] tracking-widest text-jarvis-blue/70 hover:text-jarvis-blue transition"
        >
          ← BACK
        </button>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-jarvis-blue animate-pulse rounded-full" />
          <h1 className="text-xs tracking-[0.4em] text-jarvis-blue">
            ROAD TO IRON MAN
          </h1>
        </div>
        <span className="ml-auto text-[10px] tracking-widest text-white/30">
          {progress.done}/{progress.total} RUNGS
        </span>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs text-red-300">
            {error}
          </div>
        )}

        {/* Arc progress */}
        <div className="mb-10">
          <div className="flex items-end justify-between mb-2">
            <div className="text-[10px] tracking-[0.3em] text-white/40">2026 → 2046</div>
            <div className="text-[10px] tracking-widest text-jarvis-blue">
              {currentPhase ? data?.phase_titles[currentPhase]?.toUpperCase() : 'ARC COMPLETE'}
            </div>
          </div>
          <div className="h-[3px] w-full bg-white/5">
            <div
              className="h-full bg-jarvis-blue transition-all duration-700"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
        </div>

        {/* The daily practice — the engine of the program */}
        <section className="mb-12">
          <h2 className="text-[11px] tracking-[0.3em] text-violet-300/80 mb-1">
            THE DAILY PRACTICE
          </h2>
          <p className="text-[10px] text-white/35 mb-4 leading-relaxed max-w-2xl">
            Hardware builds are episodic. Study is daily, and inertia is the binding
            constraint on the suit — not power.
          </p>
          <div className="space-y-2">
            {daily.map((item) => {
              const doneToday = isDoneToday(item);
              return (
                <div
                  key={item.id}
                  className={`border px-4 py-3 flex items-start gap-3 transition ${
                    doneToday
                      ? 'border-violet-400/40 bg-violet-400/[0.04]'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <button
                    onClick={() => markDone(item)}
                    disabled={busy === item.id}
                    className={`mt-[2px] w-4 h-4 shrink-0 border transition ${
                      doneToday
                        ? 'bg-violet-400/80 border-violet-300'
                        : 'border-white/25 hover:border-violet-300'
                    }`}
                    title={doneToday ? 'done today' : 'mark done today'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm">{item.title}</span>
                      <span className="text-[9px] tracking-widest text-white/30">
                        {item.cadence.toUpperCase()}
                      </span>
                      {item.done_count > 0 && (
                        <span className="text-[9px] text-violet-300/70">
                          ×{item.done_count}
                        </span>
                      )}
                    </div>
                    {item.detail && (
                      <p className="text-[11px] text-white/40 mt-1 leading-relaxed">
                        {item.detail}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* The arc */}
        {PHASE_ORDER.filter((p) => p !== 'daily').map((phase) => {
          const rows = items.filter((i) => i.phase === phase);
          if (!rows.length) return null;
          const isCurrent = phase === currentPhase;
          return (
            <section key={phase} className="mb-10">
              <h2
                className={`text-[11px] tracking-[0.3em] mb-3 ${
                  isCurrent ? 'text-jarvis-blue' : 'text-white/30'
                }`}
              >
                {(data?.phase_titles[phase] ?? phase).toUpperCase()}
                {isCurrent && <span className="ml-2 text-[9px]">← YOU ARE HERE</span>}
              </h2>
              <div className="space-y-2">
                {rows.map((item) => (
                  <div
                    key={item.id}
                    className={`border px-4 py-3 transition ${
                      item.status === 'done'
                        ? 'border-white/5 opacity-40'
                        : item.status === 'doing'
                        ? 'border-jarvis-blue/40 bg-jarvis-blue/[0.03]'
                        : item.status === 'blocked'
                        ? 'border-red-400/30'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => cycle(item)}
                        disabled={busy === item.id}
                        className={`mt-[2px] w-4 h-4 shrink-0 border transition ${
                          item.status === 'done'
                            ? 'bg-jarvis-blue/70 border-jarvis-blue'
                            : item.status === 'doing'
                            ? 'border-jarvis-blue bg-jarvis-blue/25'
                            : 'border-white/25 hover:border-jarvis-blue'
                        }`}
                        title={`${item.status} → ${STATUS_NEXT[item.status]}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span
                            className={`text-[9px] px-1.5 py-[1px] border tracking-widest ${
                              LANE_COLOR[item.lane] ?? 'text-white/40 border-white/20'
                            }`}
                          >
                            {item.lane}
                          </span>
                          <span className="text-sm">{item.title}</span>
                        </div>
                        {item.detail && (
                          <p className="text-[11px] text-white/40 mt-1 leading-relaxed">
                            {item.detail}
                          </p>
                        )}
                        {item.gate && (
                          <p className="text-[10px] text-amber-300/60 mt-1.5">
                            GATE — {item.gate}
                          </p>
                        )}
                        {item.note && (
                          <p className="text-[10px] text-white/50 mt-1.5 italic">
                            {item.note}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        {!items.length && !error && (
          <p className="text-xs text-white/30">Loading the board…</p>
        )}
      </main>
    </div>
  );
}
